import { domToBlob, domToPng } from 'modern-screenshot'
import { ReactNode, RefObject, useImperativeHandle, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../rapid-ui'
import { logger } from '../utilities'
import { scaleFontSizeFix, withTrailingSpacePadding } from './render-fixups'
import { trackToolClicked } from './tracking'
import { FeedAdWidget } from './widgets'

export type FeedAdToolsRef = {
	render: () => Promise<Blob>
}

export interface FeedAdToolsProps {
	children: ReactNode
	ref: RefObject<FeedAdToolsRef | null>
}

const renderOptions = {
	scale: 3,
	filter: (el: Node) => {
		if (el.nodeType !== Node.ELEMENT_NODE) {
			return true
		}
		if (
			el instanceof HTMLElement
			&& el.getAttribute('data-render-exclude') === 'true'
		) {
			return false
		}
		return true
	},
	onCloneNode: (clonedNode: Node) => {
		if (clonedNode instanceof HTMLElement) {
			if (clonedNode.tagName === 'TEXTAREA' || clonedNode.tagName === 'INPUT') {
				const style = window.getComputedStyle(clonedNode)

				if (
					style.getPropertyValue('field-sizing') === 'content'
					|| clonedNode.className.includes('[field-sizing:content]')
				) {
					console.warn('invoking automatic field-sizing fix for baked-in image for feed ad')
					const actualWidth = clonedNode.scrollWidth + 16 // Add 16px buffer for safety
					const actualHeight = clonedNode.scrollHeight + 8 // Add 8px buffer for safety

					clonedNode.style.width = `${actualWidth}px`
					clonedNode.style.height = `${actualHeight}px`
					clonedNode.style.minWidth = `${actualWidth}px`
					clonedNode.style.minHeight = `${actualHeight}px`

					clonedNode.style.setProperty('field-sizing', 'inherit')
					clonedNode.style.overflow = 'visible'
				}
			}
		}
	},
	onCloneEachNode: scaleFontSizeFix,
}

export function FeedAdTools({ children, ref }: FeedAdToolsProps) {
	const { t } = useTranslation('editor')
	const containerRef = useRef<HTMLDivElement>(null)

	useImperativeHandle(
		ref,
		() => ({
			render() {
				return new Promise((resolve, reject) => {
					const container = containerRef.current
					if (!container) {
						reject(new Error('No container found'))
						return
					}
					const renderMe = container.querySelector('[data-render-me]')
					if (renderMe) {
						withTrailingSpacePadding(renderMe, () => domToBlob(renderMe, renderOptions))
							.then(resolve)
							.catch(reject)
					} else {
						reject(new Error('No render me found'))
					}
				})
			},
		}),
		[containerRef],
	)

	const download = () => {
		const container = containerRef.current
		if (!container) {
			return
		}
		const renderMe = container.querySelector('[data-render-me]')
		if (renderMe) {
			withTrailingSpacePadding(renderMe, () => domToPng(renderMe, renderOptions)).then((png) => {
				const a = document.createElement('a')
				a.href = png
				a.download = 'feed-ad.png'
				a.click()
			}).catch((error) => {
				logger.error(error, { tags: { component: 'FeedAdTools', action: 'download-png' } })
			})
		}
	}

	return (
		<FeedAdWidget
			globalTools={
				<div className="flex items-center justify-center gap-2 p-4">
					<div className="text-sm">
						<Button
							size="inline"
							onClick={() => {
								trackToolClicked('download_png', 'feed')
								download()
							}}
						>
							{t('simulator.feedAd.downloadAsPng')}
						</Button>
					</div>
				</div>
			}
		>
			{({ widget }) => (
				<>
					<div ref={containerRef} data-render-container="feed-ad">
						{children}
					</div>
					<div className="mt-4">{widget}</div>
				</>
			)}
		</FeedAdWidget>
	)
}
